/**
 * Transport layer.
 *
 * Nothing here knows about the UI: it signs requests, turns error responses into
 * thrown `ApiError`s, and distinguishes "the server said no" (which the caller
 * must handle) from "the network is down" (which the sync queue retries).
 */

const TOKEN_KEY = 'pt.token';

/**
 * The URL this app is served from, *including any sub-path*.
 *
 * `https://example.org/portfolio/` when index.html sits at /portfolio/index.html,
 * `https://example.org/` when it sits at the root. Deriving it from the document
 * rather than from `location.origin` is what lets the app be mounted under a
 * path by a reverse proxy without rebuilding it.
 */
export function appBaseUrl() {
  return new URL('.', window.location.href).href;
}

/**
 * Where the API lives.
 *   - explicit `VITE_API_URL` always wins (separate host, or a native build).
 *   - native shells: the bundle is served from capacitor:// or http://localhost,
 *     where a relative /api would resolve to the shell itself, not the server.
 *   - otherwise: the app's own base path, so /portfolio/ finds /portfolio/api.
 */
function resolveBaseUrl() {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return String(configured).replace(/\/+$/, '');

  const isNativeShell = /^(capacitor|ionic):/.test(window.location.protocol);
  if (isNativeShell) return 'http://localhost:4000';

  return appBaseUrl().replace(/\/+$/, '');
}

const API_BASE = resolveBaseUrl();

/**
 * An address on the site the app belongs to -- the terms, the privacy policy.
 * Root-relative to the server, not to the page: a native shell's own origin
 * has no documents on it.
 */
export const siteUrl = path => new URL(path, new URL(API_BASE + '/', window.location.href)).href;

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
  get isAuthError() {
    return this.status === 401;
  }

  /**
   * The field the server refused, when it named one.
   *
   * Validation messages are shaped `"units" must be between 0 and ...`, and the
   * field is the only part of them worth keeping: the sentence itself is
   * English written for whoever reads the logs, and it was being shown verbatim
   * to somebody using the app in Polish, internal field name and all.
   */
  get field() {
    const named = /^"([A-Za-z][A-Za-z0-9_]*)"/.exec(this.message || '');
    return named ? named[1] : null;
  }

  /**
   * What the store should record, so the view can say it in the reader's
   * language: `api:<code>` or `api:<code>:<field>`.
   */
  get key() {
    return `api:${this.code}${this.field ? ':' + this.field : ''}`;
  }
}

/** Thrown when the request never reached the server. */
export class OfflineError extends Error {
  constructor(cause) {
    super('The server could not be reached.');
    this.cause = cause;
  }
}

let token = null;
try {
  token = window.localStorage.getItem(TOKEN_KEY);
} catch {
  token = null;
}

/*
 * The landing page's live preview runs this same client on the same origin as
 * the real app -- so it can read the session token of whoever is signed in on
 * this browser. Sealing drops the token and refuses every request: the preview
 * shows sample data and must never be able to reach anybody's account.
 */
let sealed = false;

export function sealForPreview() {
  sealed = true;
  token = null;
}

export function getToken() {
  return token;
}

export function setToken(next) {
  if (sealed) return;
  token = next || null;
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private browsing with storage disabled: the session lives in memory only.
  }
}

async function request(method, path, body, { idempotencyKey } = {}) {
  if (sealed) throw new OfflineError(new Error('The preview does not talk to the server.'));
  let response;
  try {
    response = await fetch(API_BASE + path, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        /* The queued change's own id: sent again, it is recognised rather than saved twice. */
        ...(idempotencyKey ? { 'idempotency-key': idempotencyKey } : {})
      },
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
  } catch (error) {
    throw new OfflineError(error);
  }

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error || 'http_error',
      payload?.message || `Request failed with ${response.status}.`
    );
  }
  return payload;
}

const get = path => request('GET', path);
const post = (path, body, options) => request('POST', path, body ?? {}, options);
const put = (path, body) => request('PUT', path, body ?? {});
const patch = (path, body) => request('PATCH', path, body ?? {});
const del = path => request('DELETE', path);

export const api = {
  request,

  health: () => get('/api/health'),

  // ---- session ----
  guest: (language, start = {}) => post('/api/auth/guest', { language, ...start }),
  register: payload => post('/api/auth/register', payload),
  login: payload => post('/api/auth/login', payload),
  me: () => get('/api/auth/me'),
  exportMe: () => get('/api/auth/me/export'),
  acceptTerms: ({ next = false } = {}) => post('/api/auth/terms', next ? { acceptTerms: true, next: true } : { acceptTerms: true }),
  review: () => get('/api/ai/review'),
  writeReview: () => post('/api/ai/review'),
  giftSeen: () => post('/api/auth/gift-seen'),
  deleteMe: body => request('DELETE', '/api/auth/me', body),
  claim: payload => post('/api/auth/claim', payload),
  setPassword: payload => post('/api/auth/password', payload),
  /** Credentials only when there is no session — see the route's comment. */
  resendVerification: credentials => post('/api/auth/resend-verification', credentials || {}),

  // ---- Discord OAuth ----
  discordStatus: () => get('/api/auth/discord/status'),
  discordStart: (returnUrl, language, start = {}) => post('/api/auth/discord/start', { returnUrl, language, ...start }),
  discordLinkStart: returnUrl => post('/api/auth/discord/link/start', { returnUrl }),
  discordExchange: ticket => post('/api/auth/discord/exchange', { ticket }),
  discordUnlink: () => del('/api/auth/discord/link'),

  // ---- ledger ----
  portfolio: () => get('/api/portfolio'),
  metrics: (month, strategy) =>
    get(`/api/portfolio/metrics?month=${encodeURIComponent(month)}` +
        (strategy ? `&strategy=${encodeURIComponent(strategy)}` : '')),
  settings: payload => put('/api/portfolio/settings', payload),

  // ---- money ----
  cashBalances: () => get('/api/money/cash'),
  setCashBalance: (currency, amount) => put(`/api/money/cash/${currency}`, { amount }),
  deleteCashBalance: currency => del(`/api/money/cash/${currency}`),
  rates: () => get('/api/money/rates'),
  setRate: (currency, rate, month) => put(`/api/money/rates/${currency}`, { rate, month }),
  deleteRate: (currency, month) => del(
    `/api/money/rates/${currency}` + (month ? `?month=${month}` : '')
  ),
  inflation: () => get('/api/money/inflation'),
  setInflation: (currency, rate) => put(`/api/money/inflation/${currency}`, { rate }),
  deleteInflation: currency => del(`/api/money/inflation/${currency}`),
  refreshRates: currencies => post('/api/money/rates/refresh', { currencies }),
  setBudget: (category, amount, currency) => put(`/api/money/budgets/${encodeURIComponent(category)}`, { amount, currency }),
  addGoal: (payload, options) => post('/api/goals', payload, options),
  updateGoal: (id, payload) => patch(`/api/goals/${id}`, payload),
  deleteGoal: id => del(`/api/goals/${id}`),
  /* Payment reminders on this device -- server/src/routes/push.routes.js. */
  pushState: endpoint => get('/api/push' + (endpoint ? '?endpoint=' + encodeURIComponent(endpoint) : '')),
  pushSubscribe: (subscription, timezone) => post('/api/push/subscriptions', { ...subscription, timezone }),
  pushUnsubscribe: endpoint => request('DELETE', '/api/push/subscriptions', { endpoint }),
  pushPrefs: prefs => put('/api/push/prefs', prefs),
  pushTest: endpoint => post('/api/push/test', { endpoint }),
  backfillRates: currencies => post('/api/money/rates/backfill', { currencies }),

  recordNetWorth: month => post('/api/portfolio/history/snapshot', { month }),

  // ---- model provider ----
  aiSettings: () => get('/api/ai/settings'),
  categorise: labels => post('/api/ai/categorise', { labels }),
  backfillPrices: range => post('/api/holdings/backfill-prices', range || {}),
  setPrice: (ticker, price, month) => put(`/api/holdings/prices/${ticker}`, { price, month }),

  adsConfig: () => get('/api/ads/config'),

  // ---- Liquidity+ (Stripe's pages do the paying; these only open and read them) ----
  billingCheckout: (interval, region, currency) => post('/api/billing/checkout', { interval, region, currency }),
  billingPortal: () => post('/api/billing/portal'),

  // Admin panel -- the server answers 404 to every other account.
  adminStats: () => get('/api/admin/stats'),
  adminAccounts: () => get('/api/admin/accounts'),
  adminSetPlan: (id, body) => post(`/api/admin/accounts/${encodeURIComponent(id)}/plan`, body),
  billingSync: sessionId => post('/api/billing/sync', { sessionId }),
  billingRefresh: () => post('/api/billing/refresh'),

  // ---- trading journal (deliberately outside the ledger) ----
  trades: () => get('/api/trades'),
  addTrade: payload => post('/api/trades', payload),
  importTrades: payload => post('/api/trades/import', payload),
  updateTrade: (id, payload) => patch(`/api/trades/${id}`, payload),
  deleteTrade: id => del(`/api/trades/${id}`),
  backtests: () => get('/api/trades/backtests'),
  addBacktest: payload => post('/api/trades/backtests', payload),
  updateBacktest: (id, payload) => patch(`/api/trades/backtests/${id}`, payload),
  deleteBacktest: id => del(`/api/trades/backtests/${id}`),
  prop: () => get('/api/prop'),
  taxConvert: items => post('/api/tax/convert', { items }),
  taxVotes: () => get('/api/tax/votes'),
  setTaxVote: (country, on) => put(`/api/tax/votes/${encodeURIComponent(country)}`, { on }),
  addPropAccount: payload => post('/api/prop/accounts', payload),
  updatePropAccount: (id, payload) => patch(`/api/prop/accounts/${id}`, payload),
  deletePropAccount: id => del(`/api/prop/accounts/${id}`),
  addPropEvent: (id, payload) => post(`/api/prop/accounts/${id}/events`, payload),
  deletePropEvent: id => del(`/api/prop/events/${id}`),

  forgotPassword: email => post('/api/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => post('/api/auth/reset-password', { token, newPassword }),

  // ---- recurring ----
  recurring: month => get('/api/recurring' + (month ? `?month=${month}` : '')),
  addRecurring: (payload, options) => post('/api/recurring', payload, options),
  updateRecurring: (id, payload) => patch(`/api/recurring/${id}`, payload),
  deleteRecurring: id => del(`/api/recurring/${id}`),
  postRecurring: (month, again = false) => post('/api/recurring/post', again ? { month, again: true } : { month }),

  // ---- bulk import ----
  importEntries: entries => post('/api/import/entries', { entries }),
  checkImport: entries => post('/api/import/entries/check', { entries }),
  saveCategoryRules: rules => post('/api/import/rules', { rules }),
  importSnapshot: snapshot => post('/api/portfolio/import', { snapshot }),
  snapshotNetWorth: month => post('/api/portfolio/history/snapshot', { month }),

  addIncome: (payload, options) => post('/api/income', payload, options),
  deleteIncome: id => del(`/api/income/${id}`),

  addExpense: (payload, options) => post('/api/expenses', payload, options),
  deleteExpense: id => del(`/api/expenses/${id}`),

  addDebt: (payload, options) => post('/api/debts', payload, options),
  updateDebt: (id, payload) => patch(`/api/debts/${id}`, payload),
  updateIncome: (id, payload) => patch(`/api/income/${id}`, payload),
  updateExpense: (id, payload) => patch(`/api/expenses/${id}`, payload),
  deleteDebt: id => del(`/api/debts/${id}`),
  payDebt: (id, payload, options) => post(`/api/debts/${id}/payments`, payload, options),

  addHolding: (payload, options) => post('/api/holdings', payload, options),
  updateHolding: (id, payload) => patch(`/api/holdings/${id}`, payload),
  deleteHolding: id => del(`/api/holdings/${id}`),
  refreshPrices: () => post('/api/holdings/refresh-prices'),
  metalPrice: ({ ticker, month, currency, unit }) => get('/api/holdings/metal-price?' + new URLSearchParams({ ticker, month, currency, unit })),

  updateVehicle: payload => put('/api/vehicle', payload),
  addFuel: (payload, options) => post('/api/vehicle/fuel', payload, options),
  updateFuel: (id, payload) => patch(`/api/vehicle/fuel/${id}`, payload),
  deleteFuel: id => del(`/api/vehicle/fuel/${id}`),
  addCarExpense: (payload, options) => post('/api/vehicle/expenses', payload, options),
  updateCarExpense: (id, payload) => patch(`/api/vehicle/expenses/${id}`, payload),
  deleteCarExpense: id => del(`/api/vehicle/expenses/${id}`)
};
